import { Empty, Spin } from 'antd'
import classNames from 'classnames'
import { ClassValue } from 'classnames/types'
import React, { ReactNode } from 'react'

import Icon from '@ant-design/icons/es/components/Icon'
import { makeStyles } from '@material-ui/styles'

import emptyImg from '../../assets/empty.svg'
import { ExpandIcon } from '../../icons'
import { them } from '../../them'
import { Button } from '../Button/Button'

const { useState, useEffect } = React

/** 行render返回的obj接口类型 */
interface ColRenderProps {
  children: JSX.Element; /** 返回为对象时渲染内容 */
  props?: {
    colSpan?: number; /** 表格内容当前列合并数量 */
    className?: ClassValue; /** className */
  };
}

/** columns 列数据接口类型 */
export interface SpaceBetweenTableColProps<T> {
  dataIndex?: keyof T; /** 当前列数据索引，key存在时可不设 */
  key?: React.Key; /** 用于循环，唯一值，dataIndex唯一时可不设 */
  colSpan?: number; /** 表头列合并数量 */
  title?: string; /** 列标题 */
  className?: ClassValue; /** className */
  render?: (text: any, record: T, index: number) => ColRenderProps | ReactNode; /** 内容列渲染函数，支持列合并，暂不支持行合并 */
}

export interface SpaceBetweenTableProps<T> {
  columns: SpaceBetweenTableColProps<T>[]; /** 表头数据 */
  data: T[]; /** 内容数据 */
  rowKey?: keyof T; /** 列表行key，请保持唯一性，一般取表格data中唯一值项 */
  className?: ClassValue; /** class名 */
  expandClassName?: ClassValue; /** 展开DIV class名 */
  loading?: boolean; /** 表格是否加载中 */
  plain?: boolean; /** 是否朴素表格，为ture，则不显示默认斑马线条纹及标题栏背景 */
  bordered?: boolean; /** 是否展示外边框和列边框 */
  showHeader?: boolean; /** 是否显示表头 */
  expandable?: boolean; /** 是否可展开 */
  defaultExpandAllRows?: boolean; /** 初始时，是否展开所有行，expandable===true有效 */
  expandRowByClick?: boolean; /** 通过点击行来展开子行，expandable===true有效 */
  defaultExpandedRowKeys?: number[]; /** 默认展开的行，数组单项为列表数字索引（0|1...），expandable===true有效 */
  expandedRowKeys?: number[]; /** 展开的行，控制属性，数组单项为列表数字索引（0|1...），expandable===true有效 */
  expandedRowRender?: (record: T) => JSX.Element; /** 行展开时渲染函数，record为当前data数据 */
  onExpand?: (currentExpandKey: number) => void; /** 点击展开图标时触发，currentExpandKey为当前展开项数字索引 */
  onExpandedRowsChange?: (expandedRowKeys: number[]) => void; /** 展开的行变化时触发，expandedRowKeys为所有展开项数字索引集合 */
  emptyImgSrc?: string; /** 空图片地址 */
}

export const SpaceBetweenTable = <T extends {}>({ columns, data, rowKey, className, expandClassName, loading, plain, bordered, showHeader = true, expandable, defaultExpandAllRows, expandRowByClick, defaultExpandedRowKeys, expandedRowKeys: controlExpandedRowKeys, expandedRowRender, onExpand, onExpandedRowsChange, emptyImgSrc }: SpaceBetweenTableProps<T>): JSX.Element => {
  const colLen = expandable ? columns.length + 1 : columns.length /** 表格列数 */
  const fullWidthStyle = { gridColumnStart: `span ${colLen}` } /** 占据整行 */
  const stripeBackground = { backgroundColor: them['color-hover-stripe'] } /** 斑马线条纹背景及鼠标滑过样式 */
  const positionZero = { top: 0, right: 0, bottom: 0, left: 0 } /** 绝对定位下，继承父元素宽高 */
  const contentLiHeight = 51 /** 表格行高 */
  const noDataImgHeight = 60 /** 暂无数据图片高 */
  const noDataHeight = noDataImgHeight + 30 /** 30这个是暂无数据marginBottom + p行高，此处用于计算loading padding，这样loading后即可平滑过度到暂无数据 */
  const loadingPadding = data.length > 0 && showHeader ? `${contentLiHeight * 2 + noDataHeight / 2}px 0` : `${contentLiHeight + noDataHeight / 2}px 0`

  /** 表格下i元素(行背景)表头及表格内容公共css */
  const iCommonCss = {
    ...fullWidthStyle,
    ...positionZero,
    zIndex: 0,
    borderBottom: plain || bordered ? `1px solid ${them['color-line']}` : 0
  }

  const classes = makeStyles({
    root: {
      border: bordered ? `1px solid ${them['color-line']}` : 0,
      borderBottom: 0,
      position: 'relative',
      display: 'grid',
      justifyContent: 'space-between',
      color: them['color-default-default'],

      /** 提供背景颜色支持，默认斑马条纹及滑过高亮 */
      '@global .thead-th': {
        ...iCommonCss,
        position: 'absolute',
        gridRowEnd: 2,

        background: plain ? 'none' : them['color-hover-default']
      },
      '@global .tbody-tr': {
        ...iCommonCss,
        position: 'absolute',

        '&:hover': { ...stripeBackground }
      },

      /** 表头及表内容 */
      '& > li > span': {
        alignSelf: 'center',
        zIndex: 1,
        padding: `${them['padding-sm']} ${them['padding-xs']}`,

        '&:first-child': {
          paddingLeft: plain && !bordered && !expandable ? 0 : them['padding-xs']
        },

        '&:last-of-type': expandable
          ? {
            padding: 0,

            '& > button': {
              padding: `calc(${them['padding-xs']} / 2) ${them['padding-xs']}`,
              boxSizing: 'content-box'
            }
          }
          : {
            paddingRight: plain && !bordered ? 0 : them['padding-xs'],
            justifySelf: plain && !bordered ? 'end' : 'auto'
          }
      },

      '& > li': {
        display: 'contents', /** 列包裹容器，本身不参与渲染 */

        [showHeader ? '&:not(:first-child) > span' : '& > span']: {
          '&:empty::after': { content: '"-"' },
          '&:hover ~ i': { ...stripeBackground }
        },

        /** 斑马线条纹 */
        '&:nth-child(2n + 1) > .tbody-tr': {
          backgroundColor: !plain ? them['color-hover-stripe'] : 'none'
        },

        '&:first-child': {
          color: showHeader ? them['color-default-secondary'] : 'inherit'
        }
      }
    },

    /** 展开内容容器 */
    expand: {
      ...fullWidthStyle,
      zIndex: 1,
      padding: them['padding-md'],
      border: `1px solid ${them['color-line']}`
    },

    /** 展开按钮旋转动画 */
    expandIcon: {
      transition: 'all .5s'
    },
    rotateArrow: {
      transform: 'rotate(-180deg)'
    },

    /** 加载中，boolean控制属性 */
    loading: {
      ...fullWidthStyle,
      ...positionZero,
      position: data.length > 0 ? 'absolute' : 'relative', /** 表格存在数据情况下，使用absolute定位 */
      zIndex: 1,
      display: 'flex',
      justifyContent: 'center',
      padding: loadingPadding,
      background: 'rgba(255, 255, 255, .35)'
    },

    /** 无数据 */
    noData: {
      ...fullWidthStyle,
      padding: `${contentLiHeight}px 0`,
      color: them['color-default-secondary'],
      textAlign: 'center',

      '& img ': {
        height: '100%'
      }
    }
  })()

  /** 展开行集合 */
  const initExpandedRowKeys = controlExpandedRowKeys || defaultExpandedRowKeys || []
  const [expandedRowKeys, setExpandedRowKeys] = useState(initExpandedRowKeys)

  useEffect(() => {
    if (controlExpandedRowKeys !== undefined) {
      /** 比较数组差异，查看数组是否存在不同项，存在则更新 */
      const isExitDiff = expandedRowKeys.some(key => controlExpandedRowKeys.includes(key))
      if (isExitDiff) {
        setExpandedRowKeys(controlExpandedRowKeys)
      }
    }
  }, [controlExpandedRowKeys])

  useEffect(() => {
    /*
    * 因为expandedRowKeys采用的是index，所以在切页时会保留上次状态
    * 例如，1、3、5行展开，那么翻页后，1、3、5照样会展开
    * 因此此处重置展开状态
    */
    setExpandedRowKeys([])

    /** expandedRowKeys属性不传情况下才考虑defaultExpandAllRows情况 */
    if (controlExpandedRowKeys === undefined && defaultExpandAllRows) {
      const allRowKeys = data.map((_, index) => index)
      setExpandedRowKeys(allRowKeys)
    }
  }, [data])

  /**
   * 处理行单击及展开图标单击事件
   * @param key 行数字序号索引
   * @param event 鼠标单击事件
   */
  const onClick: (key: number, event?: any) => void = (key, event) => {
    /** 展开行内容单击会冒泡到父元素，因此此处禁止 */
    if (event !== undefined && event.target.getAttribute('expand-row') !== 'true') {
      return
    }

    /** 展开/收缩行 */
    const filterExpandedRowKeys = expandedRowKeys.filter(item => item !== key)
    filterExpandedRowKeys.length === expandedRowKeys.length && filterExpandedRowKeys.push(key)

    /** 如果初始传入了控制属性expandedRowKeys，那么此处不更新 */
    controlExpandedRowKeys === undefined && setExpandedRowKeys(filterExpandedRowKeys)

    /** 触发onExpand函数 */
    onExpand && onExpand(key)

    /** 展开的行发生变化，触发onExpandedRowsChange */
    onExpandedRowsChange && onExpandedRowsChange(filterExpandedRowKeys)
  }

  /** 剩余列数，用于列合并计算 */
  let remainingColSpan = columns.length

  return (
    <section
      className={classNames(className, classes.root)}
      data-type="grid-table"
      data-loading={loading}
      data-plain={plain}
      data-bordered={bordered}
      data-show-header={showHeader}
      data-expandable={expandable}
      data-default-expand-all-rows={defaultExpandAllRows}
      style={{
        gridTemplateColumns: `repeat(${colLen}, auto)`
      }}
    >
      {/* 是否显示表头 */}
      {showHeader && (
        <li>
          {/* 列属性 */}
          {columns.map(({ title, dataIndex = '', key = dataIndex, colSpan = 1 }) => {
            /** 计算剩余span数 */
            remainingColSpan -= colSpan

            /** 列合并 */
            const cssInLine = { gridColumnStart: `span ${colSpan}` }

            /** 剩余span >= 0 且 当前span数 > 0 才渲染 */
            return remainingColSpan >= 0 && colSpan > 0 && (
              <span key={key.toString()} style={cssInLine}>
                {title}
              </span>
            )
          })}

          {/* 展开按钮表头，没啥展示的必要，这儿就显示一个空span */}
          {expandable && (
            <span />
          )}

          {/* 表头背景 */}
          <i className="thead-th" />
        </li>
      )}

      {/* 表格内容 */}
      {data.map((record, index) => {
        /** 在此行前面已经展开的行数 */
        const frontExpandLength = expandedRowKeys.filter(item => item < index).length

        /** 当前行开始网格位置 */
        const lineStart = index + (showHeader ? 2 : 1) + frontExpandLength

        /** 当前行是否展开 */
        const isExpand = expandedRowKeys.includes(index)

        /** 剩余列数默认为总列数，当剩余列数 < 0 时不渲染 */
        let remainingColSpan = columns.length

        /** 最好不要用索引形式 */
        const key = rowKey === undefined ? index : record[rowKey]

        return (
          <li key={String(key)} onClick={expandable && expandRowByClick ? onClick.bind(null, index) : undefined}>
            {columns.map(({ dataIndex, key = dataIndex, className, render }) => {
              let text: ReactNode = dataIndex !== undefined ? record[dataIndex] : ''
              let colSpan = 1
              let colClassName
              if (render !== undefined) {
                const temp = render(text, record, index)

                /** ReactNode中 ReactPortal 及 ReactElement 均包含key属性 */
                if (temp instanceof Object && !('key' in temp) && 'children' in temp) {
                  const { children, props = {} } = temp
                  text = children
                  colSpan = props.colSpan || colSpan
                  colClassName = props.className
                } else {
                  text = temp
                }
              }

              /** 计算剩余列数 */
              remainingColSpan -= colSpan

              /** 列合并 */
              const cssInLine = { gridColumnStart: `span ${colSpan}` }

              /** 列合并时如果不设置这条css，那么该行就会出现偏移 */
              const tempCss = remainingColSpan === 0 && colSpan > 1 ? { justifySelf: 'auto' } : {}

              return remainingColSpan >= 0 && colSpan > 0 && (
                <span key={(key || '').toString()} expand-row="true" className={classNames(className, colClassName)} style={{ ...cssInLine, ...tempCss }}>
                  {text}
                </span>
              )
            })}

            {/* 展开按钮 */}
            {expandable && (
              <span>
                <Button
                  onClick={(): void => onClick(index)}
                  icon={
                    <Icon
                      component={ExpandIcon}
                      className={classNames(classes.expandIcon, isExpand ? classes.rotateArrow : '')}
                    />
                  }
                  plain
                  link
                />
              </span>
            )}

            {/* 行背景 */}
            <i className="tbody-tr" expand-row="true" style={{ gridRowStart: lineStart, gridRowEnd: lineStart + 1, borderWidth: isExpand ? 0 : 1 }} />

            {/* 可展开，并且当前为展开状态渲染 */}
            {expandable && isExpand && (
              <div className={classNames(expandClassName, classes.expand)} style={{ borderLeftWidth: bordered ? 0 : 1, borderRightWidth: bordered ? 0 : 1 }}>
                {expandedRowRender && expandedRowRender(record)}
              </div>
            )}
          </li>
        )
      })}

      {/* 加载中 */}
      {loading && (
        <div className={classes.loading}>
          <Spin />
        </div>
      )}

      {/* 暂无数据情况，loading优先级较高，loading时不显示暂无数据 */}
      {data.length === 0 && !loading && (
        <div className={classes.noData}>
          <Empty
            image={emptyImgSrc ?? emptyImg}
            imageStyle={{ height: noDataImgHeight }}
            description={typeof rootLang !== 'undefined' ? rootLang.Web_ReferralRewards_J4 /** 暂无数据 */ : 'No Data'}
          />
        </div>
      )}
    </section >
  )
}
